import { FC, useEffect } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import ModalDialog from '../../common/components/ModalDialog';
import FilesDiffContainer from './FilesDiffContainer';
import { IssueDetails } from '../../issues/common/types/issues-types';
import { AggregationItem, Nullable } from 'common-types';
import SkeletonLoader from '../../executive-summary/components/SkeletonLoader';
import { useSnapshot } from 'valtio';
import QuickFixStore from '../stores/quick-fix-store';
import { cancelPolling, getFix } from '../actions/quick-fix-actions';
import { clearStore } from '../store-actions/quick-fix-store-actions';

const FilesDiffModalContainer: FC<
  React.PropsWithChildren<FilesDiffModalContainerProps>
> = props => {
  const theme = useTheme();
  const { open, onClose, onActionButtonClick, selectedIssue, selectedAggs } =
    props;
  const { isLoadingFileFixes, loadingError } = useSnapshot(QuickFixStore);

  useEffect(() => {
    open &&
      selectedIssue &&
      selectedAggs?.length &&
      getFix(selectedIssue, selectedAggs);
  }, [open, selectedAggs, selectedIssue]);

  useEffect(
    () => () => {
      cancelPolling();
      clearStore();
    },
    [],
  );

  return (
    <ModalDialog
      maxWidth='xl'
      modalColor={theme.palette.info.main}
      aria-label='Suggested Code Fix'
      open={open}
      onClose={onClose}
      modalHeader='Suggested Code Fix'>
      <Box
        p={2}
        display='flex'
        flexDirection='column'
        position='relative'
        height={500}>
        {loadingError ? (
          <Box
            height='100%'
            flexGrow={1}
            width='100%'
            display='flex'
            alignItems='center'
            justifyContent='center'>
            <Typography variant='h6' color='text.primary'>
              Failed to load Suggested Code Fix
            </Typography>
          </Box>
        ) : (
          <SkeletonLoader
            loading={isLoadingFileFixes}
            message='Pulling the latest code to show the fix. Please wait, as this operation may take a few minutes.'>
            <FilesDiffContainer
              selectedIssue={selectedIssue}
              selectedAggs={selectedAggs}
              onActionButtonClick={onActionButtonClick}
              onClose={onClose}
            />
          </SkeletonLoader>
        )}
      </Box>
    </ModalDialog>
  );
};

interface FilesDiffModalContainerProps {
  open: boolean;
  selectedIssue: Nullable<IssueDetails>;
  selectedAggs: AggregationItem[];
  onClose: () => void;
  onActionButtonClick: () => void;
}

export default FilesDiffModalContainer;
